import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionTemplate } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PERSONAS } from "../../data/whitespaces";
import { Reveal, MaskLine } from "../../lib/motionPrimitives";
import PersonaModal from "../PersonaModal";

const PersonaChapter = ({ persona, flip, onOpen }) => {
  const ref = useRef(null);
  const warm = parseInt(persona.index, 10) % 2 === 0;
  const A = {
    text: warm ? "text-ember" : "text-teal",
    dot: warm ? "bg-ember" : "bg-teal",
    metric: warm ? "text-ember" : "text-teal",
    hoverBtn: warm ? "hover:border-ember hover:text-ember" : "hover:border-teal hover:text-teal",
    hoverArrow: warm
      ? "group-hover:border-ember group-hover:bg-ember"
      : "group-hover:border-teal group-hover:bg-teal",
    overlay: warm ? "bg-ember/10" : "bg-teal/10",
  };
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const numY = useTransform(scrollYProgress, [0, 1], [70, -70]);
  // Colorist: greyscale when far, full colour when the persona is centered on screen.
  const gray = useTransform(scrollYProgress, [0.1, 0.36, 0.64, 0.9], [1, 0, 0, 1]);
  const imgFilter = useMotionTemplate`grayscale(${gray})`;

  return (
    <div
      ref={ref}
      data-testid={`persona-${persona.index}`}
      className="relative grid min-h-[60vh] items-center gap-10 border-t border-white/5 py-12 md:grid-cols-2 md:gap-20 md:py-20"
    >
      {/* Image */}
      <button
        onClick={() => onOpen(persona)}
        data-testid={`persona-open-${persona.index}`}
        className={`group relative block text-left ${flip ? "md:order-2" : ""}`}
      >
        <motion.span
          style={{ y: numY }}
          className="pointer-events-none absolute -top-12 left-0 z-0 font-display text-[7rem] font-bold leading-none text-white/[0.16] md:-top-24 md:text-[15rem]"
          aria-hidden
        >
          {persona.index}
        </motion.span>
        <div className="relative z-10 aspect-[4/3] overflow-hidden rounded-sm border border-white/10">
          <motion.img
            src={persona.image}
            alt={persona.name}
            loading="lazy"
            style={{ y: imgY, scale: 1.18, filter: imgFilter }}
            className="h-full w-full object-cover"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent opacity-70" />
          <div className={`pointer-events-none absolute inset-0 ${A.overlay} opacity-0 mix-blend-overlay transition-opacity duration-700 group-hover:opacity-100`} />
          <div className="absolute inset-x-5 bottom-5 flex items-end justify-between">
            <span className={`font-body text-[11px] uppercase tracking-[0.25em] ${A.text}`}>
              {persona.tagline}
            </span>
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/30 bg-black/30 text-white backdrop-blur transition-all duration-300 ${A.hoverArrow} group-hover:text-black`}>
              <ArrowUpRight size={18} />
            </span>
          </div>
        </div>
      </button>

      {/* Copy */}
      <div className={`relative z-10 ${flip ? "md:order-1" : ""}`}>
        <span className="font-body text-[11px] uppercase tracking-[0.3em] text-white/35">
          Persona {persona.index}
        </span>
        <h3 className="mt-3 font-display text-4xl font-medium leading-[1.05] tracking-tight text-white md:text-6xl lg:text-7xl">
          <MaskLine className="pb-[0.1em]">{persona.name}</MaskLine>
        </h3>

        <Reveal delay={0.1} className="mt-6 max-w-lg">
          <p className="font-body text-base leading-relaxed text-white/60">{persona.profile}</p>
        </Reveal>

        {/* Case study snapshot */}
        <Reveal delay={0.18} className="mt-8 max-w-lg">
          <div
            className="rounded-lg border border-white/10 bg-white/[0.02] p-6 backdrop-blur-xl"
            style={{ boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.05)" }}
          >
            <div className="flex items-center gap-3">
              <span className={`h-1.5 w-1.5 rounded-full ${A.dot}`} />
              <span className="font-body text-[11px] uppercase tracking-[0.2em] text-white/45">
                {persona.case.brand} — {persona.case.project}
              </span>
            </div>
            <div className="mt-5 flex flex-wrap gap-x-10 gap-y-4">
              {persona.case.metrics.map((m) => (
                <div key={m.label}>
                  <div className={`font-display text-3xl font-semibold ${A.metric} md:text-4xl`}>
                    {m.value}
                  </div>
                  <div className="mt-1 font-body text-[11px] uppercase tracking-[0.12em] text-white/45">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.24}>
          <button
            onClick={() => onOpen(persona)}
            data-testid={`persona-deepdive-${persona.index}`}
            className={`group mt-8 inline-flex items-center gap-3 border-b border-white/20 pb-1 font-body text-[13px] uppercase tracking-[0.2em] text-white transition-colors duration-300 ${A.hoverBtn}`}
          >
            View detailed case study
            <ArrowUpRight className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" size={16} />
          </button>
        </Reveal>
      </div>
    </div>
  );
};

export const Personas = ({ onScrollTo }) => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const openFromEvent = (e) => {
      const persona = PERSONAS.find((p) => p.index === e.detail?.index);
      if (persona) setSelected(persona);
    };
    window.addEventListener("whitespaces:open-persona", openFromEvent);
    return () => window.removeEventListener("whitespaces:open-persona", openFromEvent);
  }, []);

  return (
    <section id="personas" data-testid="personas-section" className="relative px-6 pt-14 md:px-12 md:pt-20">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-8 flex items-center gap-4">
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-teal">02 — Audience</span>
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-white/30">Whitespaces</span>
        </div>
        <h2 className="max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-white md:text-6xl lg:text-7xl">
          <MaskLine className="pb-[0.08em]">Six strategic</MaskLine>
          <MaskLine delay={0.08} className="pb-[0.08em] text-white/40">
            personas.
          </MaskLine>
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-xl font-body text-base leading-relaxed text-white/55">
            We segment the top of the funnel into six living audiences — each mapped to real
            behavior, activated with the right format, and proven by real-world campaigns. Open any
            persona for its full offering, case study and data activation.
          </p>
        </Reveal>

        <div className="mt-10">
          {PERSONAS.map((p, i) => (
            <PersonaChapter key={p.index} persona={p} flip={i % 2 === 1} onOpen={setSelected} />
          ))}
        </div>
      </div>

      <PersonaModal
        persona={selected}
        onClose={() => setSelected(null)}
        onBookDemo={() => {
          setSelected(null);
          setTimeout(() => onScrollTo("contact"), 120);
        }}
      />
    </section>
  );
};

export default Personas;
